import type { TemplateDependencyValidationError } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'

import { useTemplateContext } from '../../context/templateContext'
import type { ValidationFieldValue } from '../../types/templateModel'
import type { ValidationErrorWithLocation } from '../../uiModels/errors/types'
import { createLiteralTypeInformation } from '../fplusExpression/expressionType'
import { ValidationFieldValueWithError } from './ValidationFieldValueWithError'

export type ValidationField = keyof Omit<
  ValidationFieldValue,
  'path' | 'origin'
>

type Props = {
  value: ValidationFieldValue
  onChange?: (nextValue: ValidationFieldValue) => Promise<void>
  errors?: Record<
    ValidationField,
    ValidationErrorWithLocation<TemplateDependencyValidationError>[]
  >
}

/**
 * Table for showing each of the fields inside a ValidationFieldValue type.
 */
export const ValidationFieldValueTable = ({
  value,
  onChange,
  errors,
}: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()

  assertNotEmpty(templateVersion)

  return (
    <div className="flex flex-col gap-y-2">
      <ValidationFieldValueWithError
        label={translate('validationViewer.field.validation')}
        expression={value.validation?.expression}
        value={value}
        field="validation"
        expressionPath={`${value.path}.validation`}
        onChange={onChange}
        errors={(errors ?? { validation: [] })['validation']}
        typeInformation={createLiteralTypeInformation({
          literalType: 'boolean',
          source: value.validation?.expression?.fplusSource,
        })}
        fromOrigin={value.validation?.origin}
      />
      <ValidationFieldValueWithError
        label={translate('validationViewer.field.defaultPattern')}
        expression={value.defaultPattern?.expression}
        value={value}
        field="defaultPattern"
        expressionPath={`${value.path}.defaultPattern`}
        onChange={onChange}
        errors={(errors ?? { defaultPattern: [] })['defaultPattern']}
        typeInformation={createLiteralTypeInformation({
          literalType: 'string',
          source: value.defaultPattern?.expression?.fplusSource,
        })}
        fromOrigin={value.defaultPattern?.origin}
      />
      <ValidationFieldValueWithError
        label={translate('validationViewer.field.key')}
        expression={value.key?.expression}
        value={value}
        field="key"
        expressionPath={`${value.path}.key`}
        onChange={onChange}
        errors={(errors ?? { key: [] })['key']}
        typeInformation={createLiteralTypeInformation({
          literalType: 'string',
          source: value.key?.expression?.fplusSource,
        })}
        fromOrigin={value.key?.origin}
      />
      <ValidationFieldValueWithError
        label={translate('validationViewer.field.keyArguments')}
        expression={value.keyArguments?.expression}
        value={value}
        field="keyArguments"
        expressionPath={`${value.path}.keyArguments`}
        onChange={onChange}
        errors={(errors ?? { keyArguments: [] })['keyArguments']}
        fromOrigin={value.keyArguments?.origin}
      />
    </div>
  )
}
